<template>
  <div class="page-container">
    <div class="main">
      <el-card class="tps-card" shadow="never">
        <div slot="header" class="tps-card-header">
          <span>{{ $t('text.realtimeTps') }}</span>
          <el-button size="mini" type="primary" :loading="tpsLoading" @click="fetchTps">
            {{ $t('text.refresh') }}
          </el-button>
        </div>
        <el-form :inline="true" size="small" class="tps-form">
          <el-form-item :label="$t('text.paymentChannel')">
            <el-select v-model="tpsForm.channelId" filterable clearable :placeholder="$t('message.chooseOne')">
              <el-option :label="$t('option.all')" value=""></el-option>
              <el-option v-for="item in payOption"
                         :key="item.channelId"
                         :label="item.channelId + ' - ' + item.channelName"
                         :value="item.channelId"/>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.channelType')">
            <el-select v-model="tpsForm.payType" filterable clearable :placeholder="$t('message.chooseOne')">
              <el-option :label="$t('text.collection')" :value="PAYMENT_TYPE_ID_COLLECTION()"/>
              <el-option :label="$t('text.disbursement')" :value="PAYMENT_TYPE_ID_PAYMENT()"/>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.windowSeconds')">
            <el-input-number v-model="tpsForm.windowSeconds" :min="10" :max="600" :step="10"/>
          </el-form-item>
          <el-form-item :label="$t('text.autoRefresh')">
            <el-switch v-model="tpsForm.autoRefresh"/>
          </el-form-item>
        </el-form>
        <el-table v-if="tpsByChannel.length > 0" :data="tpsByChannel" size="mini" class="tps-table">
          <el-table-column prop="channelId" :label="$t('text.channelId')" width="140"/>
          <el-table-column prop="channelName" :label="$t('text.channelName')" min-width="160"/>
          <el-table-column prop="cnt" :label="$t('text.transactionCount')" width="120"/>
          <el-table-column prop="successCnt" :label="$t('text.transactionSuccessCount')" width="140"/>
          <el-table-column prop="failCnt" :label="$t('text.transactionFailCount')" width="120"/>
          <el-table-column prop="timeoutCnt" :label="$t('text.timeoutCount')" width="120"/>
          <el-table-column prop="successRate" :label="$t('text.transactionSuccessRate')" width="130"/>
          <el-table-column prop="callbackRate" :label="$t('text.callbackSuccessRate')" width="130"/>
          <el-table-column prop="avgAmount" :label="$t('text.avgAmount')" width="140"/>
          <el-table-column prop="avgSuccessAmount" :label="$t('text.avgSuccessAmount')" width="160"/>
          <el-table-column prop="avgTps" :label="$t('text.tpsAvg')" width="120" :formatter="formatTpsDisplay"/>
          <el-table-column prop="avgSuccessTps" :label="$t('text.tpsSuccessAvg')" width="140" :formatter="formatTpsDisplay"/>
        </el-table>
      </el-card>
    </div>
  </div>
</template>

<script>
import {PAYMENT_TYPE_ID_COLLECTION, PAYMENT_TYPE_ID_PAYMENT} from "@/utils/Constant";

export default {
  name: "RealtimeTps",
  components: {},
  data() {
    return {
      payOption: [],
      tpsLoading: false,
      tpsForm: {
        channelId: "",
        payType: "",
        windowSeconds: 30,
        autoRefresh: false
      },
      tpsTimer: null,
      tpsRefreshIntervalMs: 30000,
      tpsByChannel: []
    };
  },
  methods: {
    PAYMENT_TYPE_ID_PAYMENT() {
      return PAYMENT_TYPE_ID_PAYMENT;
    },
    PAYMENT_TYPE_ID_COLLECTION() {
      return PAYMENT_TYPE_ID_COLLECTION;
    },
    fetchTps() {
      if (!this.tpsForm.payType) {
        this.$message.error(this.$t('message.chooseOne'));
        return;
      }
      const windowSeconds = Number(this.tpsForm.windowSeconds || 60);
      const fixedDate = new Date("2026-01-28T18:00:00.000");
      const endTimeL = fixedDate.getTime();
      const startTimeL = endTimeL - windowSeconds * 1000;
      const params = {
        payType: this.tpsForm.payType,
        channelId: this.tpsForm.channelId || undefined,
        startTimeL,
        endTimeL
      };
      this.tpsLoading = true;
      this.$axios
          .get("/statistics-channel/tps", {params})
          .then(res => {
            if (res.code === 200) {
              const list = Array.isArray(res.data) ? res.data : [];
              this.buildTpsSummary(list, windowSeconds);
            } else {
              this.$notify.error({
                title: this.$t('text.prompt'),
                message: res.msg,
              });
            }
          })
          .finally(() => {
            this.tpsLoading = false;
          });
    },
    buildTpsSummary(list, windowSeconds) {
      const channelAgg = new Map();
      const seedChannels = this.tpsForm.channelId
          ? [{channelId: this.tpsForm.channelId, channelName: ""}]
          : this.payOption;
      seedChannels.forEach(item => {
        if (!item || !item.channelId) return;
        channelAgg.set(item.channelId, {
          channelId: item.channelId,
          cnt: 0,
          successCnt: 0,
          failCnt: 0,
          timeoutCnt: 0,
          callbackSuccessCnt: 0,
          totalAmount: 0,
          successAmount: 0
        });
      });
      list.forEach(item => {
        const channelId = item.channelId || "";
        if (channelId) {
          const c = channelAgg.get(channelId) || {
            channelId,
            cnt: 0,
            successCnt: 0,
            failCnt: 0,
            timeoutCnt: 0,
            callbackSuccessCnt: 0,
            totalAmount: 0,
            successAmount: 0
          };
          c.cnt += Number(item.cnt || 0);
          c.successCnt += Number(item.successCnt || 0);
          c.failCnt += Number(item.failCnt || 0);
          c.timeoutCnt += Number(item.timeoutCnt || 0);
          c.callbackSuccessCnt += Number(item.callbackSuccessCnt || 0);
          c.totalAmount += Number(item.totalAmount || 0);
          c.successAmount += Number(item.successAmount || 0);
          channelAgg.set(channelId, c);
        }
      });

      const seconds = Math.max(1, Number(windowSeconds || 1));
      const channelNameMap = new Map(this.payOption.map(item => [item.channelId, item.channelName]));
      this.tpsByChannel = Array.from(channelAgg.values()).map(item => {
        const successRate = item.cnt > 0 ? (item.successCnt / item.cnt) * 100 : 0;
        const callbackRate = item.successCnt > 0 ? (item.callbackSuccessCnt / item.successCnt) * 100 : 0;
        const avgAmount = item.cnt > 0 ? item.totalAmount / item.cnt : 0;
        const avgSuccessAmount = item.successCnt > 0 ? item.successAmount / item.successCnt : 0;
        return {
          channelId: item.channelId,
          channelName: channelNameMap.get(item.channelId) || "",
          cnt: item.cnt,
          successCnt: item.successCnt,
          failCnt: item.failCnt,
          timeoutCnt: item.timeoutCnt,
          successRate: successRate.toFixed(2) + "%",
          callbackRate: callbackRate.toFixed(2) + "%",
          avgAmount: avgAmount.toFixed(2),
          avgSuccessAmount: avgSuccessAmount.toFixed(2),
          avgTps: item.cnt / seconds,
          avgSuccessTps: item.successCnt / seconds,
        };
      }).sort((a, b) => Number(b.avgTps) - Number(a.avgTps));
    },
    formatTpsDisplay(row, column, cellValue) {
      const value = Number(cellValue || 0);
      return value.toFixed(3);
    },
    startTpsTimer() {
      this.stopTpsTimer();
      this.fetchTps();
      this.tpsTimer = setInterval(() => {
        this.fetchTps();
      }, this.tpsRefreshIntervalMs);
    },
    stopTpsTimer() {
      if (this.tpsTimer) {
        clearInterval(this.tpsTimer);
        this.tpsTimer = null;
      }
    }
  },
  watch: {
    "tpsForm.autoRefresh"(val) {
      if (val) {
        this.startTpsTimer();
      } else {
        this.stopTpsTimer();
      }
    }
  },
  created() {
    this.$axios
        .get('/routeChannelInfo/selectByCondition', {params: {}})
        .then(res => {
          res.data.map(item => {
            this.payOption.push(item)
          })
          this.fetchTps();
        });
    this.tpsForm.payType = this.PAYMENT_TYPE_ID_COLLECTION();
  },
  beforeDestroy() {
    this.stopTpsTimer();
  }
};
</script>

<style lang="scss" scoped>
.tps-card {
  margin-bottom: 16px;
}

.tps-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tps-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}

.tps-form ::v-deep .el-form-item {
  margin-bottom: 0;
  margin-right: 0;
}

.tps-table {
  margin-top: 12px;
}

.ml-16 {
  margin-left: 16px;
}
</style>

